import _ from 'lodash';

import {
  AUTH_DESTINATION_SELECTED_API_PARAM,
  AUTH_EXPECTED_IDENTIFIER_PARAM,
  AUTH_ID_PARAM,
  OAUTH_ACCOUNT_ID_PARAM,
  OAUTH_SHARED_PARAM,
  connectGoogleSuiteAuths,
} from '@zapier/common-utils';

import * as Account from 'app/entities/Account';
import * as EntityActions from 'app/entity/EntityActions';
import * as statuses from 'app/entity/utils/statuses';
import AuthActions from 'app/auth/AuthActions';
import buildEntityActions from 'app/entity/utils/buildEntityActions';
import buildEntityApi from 'app/entity/utils/buildEntityApi';
import buildEntitySelectors from 'app/entity/utils/buildEntitySelectors';
import { NEW_AUTH_FORM } from 'app/auth/utils/constants';
import { addTruthyParamsToUrl } from 'app/common/utils/UrlUtils';
import { formatSuccess } from 'app/entity/utils/entityUtils';
import { normalizePostError } from 'app/entity/utils/normalizeFetchError';
import { postJson } from 'app/common/fetch';
import { preloadItems } from 'app/common/preload';

export const type = 'authentication';

const PARAMS = 'auth_fields';

export const selectors = buildEntitySelectors(type, sel => {
  sel.all.authEntity = _.partial(
    sel.all.findBy,
    _.partial(sel.stateItem, 'isNewAuth')
  );
  sel.formParams = _.partial(sel.dataItem, PARAMS);
  sel.redirectUrl = _.partial(sel.dataItem, 'url');
  sel.isShared = _.partial(sel.dataItem, 'is_shared');
  sel.all.anyAreSaving = _.partial(sel.all.hasAnyBy, sel.isSaving);
  sel.all.anyAreTesting = _.partial(sel.all.hasAnyBy, sel.isTesting);
  sel.all.anyAreFailures = _.partial(sel.all.hasAnyBy, sel.isFailure);
  sel.isConfirmationSending = entity =>
    sel.stateItem('confirmationStatus', entity) === statuses.SAVING;
  sel.isConfirmationSent = entity =>
    sel.stateItem('confirmationStatus', entity) === statuses.SUCCESS;
  sel.isConfirmationError = entity =>
    sel.stateItem('confirmationStatus', entity) === statuses.FAILURE;
});

export const api = buildEntityApi(type, '/api/v3/auths', authApi => {
  authApi.test = id => postJson(authApi.modelEndpoint(`/${id}/test`));
  authApi.initializeOauth = formParams =>
    postJson(addTruthyParamsToUrl(preloadItems.oauthUrl, formParams));
  authApi.sendConfirmation = data =>
    postJson(authApi.collectionEndpoint(), data);
});

export const actions = buildEntityActions(type, selectors, api, authActions => {
  authActions.updateFormParams = _.partial(
    authActions.updateEntityDataItem,
    PARAMS
  );

  authActions.test = (messages, id) => {
    return {
      ...EntityActions.fetchEntity(statuses.TESTING, type, messages, id),
      formatError: _.partial(normalizePostError, type, messages),
      formatSuccess: _.partial(formatSuccess, messages),
      promise: api.test(id),
    };
  };

  authActions.authorize = authId => {
    return (dispatch, getState) => {
      const originalEntity = selectors.all.entity(NEW_AUTH_FORM, getState());
      const cid = selectors.cid(originalEntity);
      const formData = selectors.data(originalEntity);
      if (
        !selectors.all.anyAreSaving(getState()) &&
        !selectors.all.anyAreTesting(getState()) &&
        !selectors.all.anyAreFailures(getState())
      ) {
        if (authId) {
          dispatch(
            authActions.updateEntityDataItem(
              AUTH_ID_PARAM,
              authId,
              NEW_AUTH_FORM
            )
          );
        }

        return dispatch(authActions.createEntity(null, NEW_AUTH_FORM)).then(
          () => {
            const newEntity = selectors.all.entityByCid(cid, getState());
            if (selectors.isSuccess(newEntity)) {
              return dispatch(
                authActions.test(null, selectors.id(newEntity))
              ).then(() => {
                const resultEntity = selectors.all.entityByCid(cid, getState());
                if (selectors.isSuccess(resultEntity)) {
                  AuthActions.postSuccess(selectors.id(newEntity));
                } else {
                  dispatch(AuthActions.initializeCollections());
                  dispatch(
                    authActions.updateEntityData(formData, NEW_AUTH_FORM)
                  );
                }
              });
            } else {
              dispatch(
                authActions.updateEntityStateItem(
                  'confirmationStatus',
                  statuses.FAILURE,
                  NEW_AUTH_FORM
                )
              );
            }
            return undefined;
          }
        );
      }
      return undefined;
    };
  };

  authActions.initializeOauth = (tempId, formParams) => {
    return EntityActions.createEntity(
      type,
      () => formParams,
      // This isn't RESTful because we're only starting the OAuth process,
      // but no `Auth` model has actually been created yet and therefore
      // no `id` will be returned from the API. Entities require an `id`
      // though so we need to fake it here and keep the `tempId`.
      params =>
        api.initializeOauth(params).then(data => ({ ...data, id: tempId })),
      { failure: 'Error initializing OAuth' },
      tempId
    );
  };

  authActions.authorizeOauth = authId => {
    return (dispatch, getState) => {
      const entity = selectors.all.entity(NEW_AUTH_FORM, getState());
      const cid = selectors.cid(entity);
      const id = selectors.id(entity);
      const _queryAttrs = ['router', 'current', 'query'];
      const formParams = _.pickBy(
        {
          ...selectors.formParams(entity),
          [OAUTH_ACCOUNT_ID_PARAM]: Account.selectors.all.currentAccountId(
            getState()
          ),
          [AUTH_DESTINATION_SELECTED_API_PARAM]: _.get(getState(), [
            ..._queryAttrs,
            AUTH_DESTINATION_SELECTED_API_PARAM,
          ]),
          [AUTH_EXPECTED_IDENTIFIER_PARAM]: _.get(getState(), [
            ..._queryAttrs,
            AUTH_EXPECTED_IDENTIFIER_PARAM,
          ]),
          [OAUTH_SHARED_PARAM]: selectors.isShared(entity),
        },
        _.negate(_.isUndefined)
      );
      if (
        !selectors.all.anyAreSaving(getState()) &&
        !selectors.all.anyAreTesting(getState()) &&
        !selectors.all.anyAreFailures(getState())
      ) {
        if (authId) {
          formParams[AUTH_ID_PARAM] = authId;
        }

        return dispatch(authActions.initializeOauth(id, formParams)).then(
          () => {
            const resultEntity = selectors.all.entityByCid(cid, getState());
            if (selectors.isSuccess(resultEntity)) {
              window.location.href = selectors.redirectUrl(resultEntity);
            }
          }
        );
      }
      return undefined;
    };
  };

  authActions.sendConfirmation = () => {
    return (dispatch, getState) => {
      const entity = selectors.all.entity(NEW_AUTH_FORM, getState());
      const data = _.cloneDeep(selectors.data(entity));
      // Sending a confirmation code is almost exactly like creating an auth
      // except the server won't return an auth model; it'll generate a
      // `code` that is sent to the user. When the user actually enters a
      // `code` then we'll try to create the auth, but during confirmation
      // we _cannot_ send the `code` or else the server will think we're
      // trying to create the auth instead of create/send a confirmation code.
      // Therefore we must `delete` the `code`.
      if (_.get(data, [PARAMS, 'code']) !== undefined) {
        delete data[PARAMS].code;
      }
      dispatch(
        authActions.updateEntityStateItem(
          'confirmationStatus',
          statuses.SAVING,
          NEW_AUTH_FORM
        )
      );
      return api
        .sendConfirmation(data)
        .then(() => {
          dispatch(
            authActions.updateEntityStateItem(
              'confirmationStatus',
              statuses.SUCCESS,
              NEW_AUTH_FORM
            )
          );
        })
        .catch(() => {
          dispatch(
            authActions.updateEntityStateItem(
              'confirmationStatus',
              statuses.FAILURE,
              NEW_AUTH_FORM
            )
          );
        });
    };
  };

  authActions.connectGoogleSuiteAuths = () => dispatch => {
    return connectGoogleSuiteAuths().then(() => {
      return dispatch(AuthActions.loadAuths());
    });
  };
});
